import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled } from '@mui/material/styles';
import DashboardLayout from '../Layouts/DashboardLayout';
import GMBService from '../../services/GMBService';
import GMBSyncService from '../../services/GMBSyncService';
import tokenManager from '../../auth/TokenManager';
import { getSession } from '../../utils/authUtils';
import cacheManager from '../../utils/cacheManager';
import { useDefaultProfile } from '../../contexts/DefaultProfileContext';
import { useTutorial } from '../../contexts/TutorialContext';
import api from '../../services/api';
import PaymentService from '../../services/PaymentService';

import PageHeader from './components/PageHeader';
import BusinessTable from './components/BusinessTable';

import logger from '../../utils/logger';
// .env-driven config
const envUseMockGmb = (process.env.REACT_APP_USE_MOCK_GMB || 'false') === 'true';
const envGoogleAccessToken = process.env.REACT_APP_GOOGLE_ACCESS_TOKEN || '';

const envBusinessName = process.env.REACT_APP_BUSINESS_NAME || 'E2E Networks Limited';
const envGoogleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const envGoogleClientSecret = process.env.REACT_APP_GOOGLE_CLIENT_SECRET || '';
const envGmbApiKey = process.env.REACT_APP_GMB_API_KEY || '';
const envGmbProjectId = process.env.REACT_APP_GMB_PROJECT_ID || '';
const envUseLiveApi = (process.env.REACT_APP_USE_LIVE_API || 'true') === 'true';
const envDebugMode = (process.env.REACT_APP_DEBUG_MODE || 'false') === 'true';

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '400px',
  gap: '16px'
}));

const NoBusinessesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '400px',
  gap: '16px',
  textAlign: 'center'
}));

const CreateBusinessButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0B91D6',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#0277BD'
  }
}));

// Helper function to get GMB score from competitor analysis localStorage
const getCompetitorAnalysisScore = (businessName) => {
  if (!businessName) return null;
  try {
    const storageKey = `visibeen_competitor_analysis_${businessName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_multi_keywords`;
    const savedData = localStorage.getItem(storageKey);
    if (!savedData) return null;

    const parsedData = JSON.parse(savedData);
    const keywords = Object.keys(parsedData);
    if (keywords.length === 0) return null;

    // Filter out keywords with invalid timestamps before comparing
    const validKeywords = keywords.filter(kw => {
      const ts = parsedData[kw]?.timestamp;
      if (!ts) return false;
      const date = new Date(ts);
      return !isNaN(date.getTime()) && date.getFullYear() >= 2000;
    });

    if (validKeywords.length === 0) return null;

    // Get the most recent keyword entry
    const mostRecentKeyword = validKeywords.reduce((latest, current) => {
      const latestTime = new Date(parsedData[latest]?.timestamp);
      const currentTime = new Date(parsedData[current]?.timestamp);
      return currentTime > latestTime ? current : latest;
    });

    const recentEntry = parsedData[mostRecentKeyword];
    if (recentEntry?.myBusinessScore && typeof recentEntry.myBusinessScore === 'number') {
      return Math.round(recentEntry.myBusinessScore);
    }
    return null;
  } catch (err) {
    logger.debug('Error getting competitor analysis score:', err);
    return null;
  }
};

const Dashboard = () => {
  const [businesses, setBusinesses] = useState([]);
  const [subscriptionsByGmb, setSubscriptionsByGmb] = useState({});
  const [accountName, setAccountName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { setDefaultProfile, clearDefaultProfile } = useDefaultProfile();

  // Tutorial context for new user onboarding
  const { startTutorial, isTutorialComplete, isActive: tutorialActive } = useTutorial();

  // Start tutorial for new users after businesses are loaded
  useEffect(() => {
    if (!loading && businesses.length > 0 && !tutorialActive && !isTutorialComplete()) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        logger.log('🎓 Starting new user tutorial...');
        startTutorial(0);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loading, businesses.length, tutorialActive, isTutorialComplete, startTutorial]);
  // Cleanup admin session data when tab/window closes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const adminAccess = params.get('adminAccess');

    if (adminAccess === 'true') {
      const handleBeforeUnload = () => {
        // Clean up admin session data when closing the tab
        try {
          clearDefaultProfile();
          sessionStorage.removeItem('adminAccessData');
          sessionStorage.removeItem('adminViewingUserId');
          sessionStorage.removeItem('currentUserContext');
          sessionStorage.removeItem('visibeen_default_profile');
          sessionStorage.removeItem('googleAccessToken');
          sessionStorage.removeItem('adminView_googleAccessToken');
          localStorage.removeItem('latestAdminAccessTempKey');
          logger.log('🧹 [Dashboard] Cleaned up admin session data on tab close');
        } catch (e) {
          logger.warn('Failed to cleanup admin session:', e);
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [location.search, clearDefaultProfile]);

  // Display current configuration when debug mode is enabled
  useEffect(() => {
    if (envDebugMode) {
      logger.log('Dashboard Environment Configuration:', {
        useLiveApi: envUseLiveApi,
        useMockGmb: envUseMockGmb,
        hasGoogleClientId: !!envGoogleClientId,
        hasGoogleClientSecret: !!envGoogleClientSecret,
        hasGmbApiKey: !!envGmbApiKey,
        hasGmbProjectId: !!envGmbProjectId,
        businessName: envBusinessName,
        debugMode: envDebugMode
      });
    }
  }, []);

  // ✅ Clear cache when component unmounts or navigates away
  useEffect(() => {
    // Track if this is the first mount
    const isFirstMount = sessionStorage.getItem('dashboard_first_mount') !== 'true';

    if (!isFirstMount) {
      // Not the first mount, so clear cache to get fresh data
      logger.log('🔄 [Dashboard] Clearing cache on navigation back to dashboard');
      cacheManager.remove('dashboard_businesses');
    }

    sessionStorage.setItem('dashboard_first_mount', 'true');

    return () => {
      // Clear the first mount flag when navigating away
      sessionStorage.removeItem('dashboard_first_mount');
    };
  }, []);

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        // Allow providing a token via query param for dev: /dashboard?token=...
        const params = new URLSearchParams(location.search);
        const tokenFromUrl = params.get('token');
        const adminAccess = params.get('adminAccess');
        let userId = params.get('userId');
        const tempKey = params.get('tempKey');

        // If adminAccess=true but no userId, try to extract from tempKey payload
        if (adminAccess === 'true' && !userId && tempKey) {
          const tempPayload = sessionStorage.getItem(tempKey) || localStorage.getItem(tempKey);
          if (tempPayload) {
            try {
              const parsed = JSON.parse(tempPayload);
              if (parsed?.targetUserId) {
                userId = String(parsed.targetUserId);
                logger.log('🔑 Extracted userId from tempKey payload:', userId);
              }
            } catch (_) { }
          }
        }

        // CRITICAL: Set adminViewingUserId in sessionStorage IMMEDIATELY when we have it
        // This ensures all API calls (including fallback paths) include the userId
        if (adminAccess === 'true' && userId) {
          logger.log('🔑 Setting adminViewingUserId in sessionStorage:', userId);
          try {
            sessionStorage.setItem('adminViewingUserId', String(userId));
            // Also set adminAccessData if we have tempKey payload
            if (tempKey) {
              const tempPayload = sessionStorage.getItem(tempKey) || localStorage.getItem(tempKey);
              if (tempPayload && !sessionStorage.getItem('adminAccessData')) {
                sessionStorage.setItem('adminAccessData', tempPayload);
              }
            }
          } catch (storageError) {
            logger.warn('[Dashboard] Unable to persist adminViewingUserId:', storageError);
          }
        }

        // Handle admin access to user's GMB profile
        if (adminAccess === 'true' && userId) {
          logger.log('🔑 Admin accessing user GMB profile:', { userId });


          let adminAccessData = sessionStorage.getItem('adminAccessData');
          if (!adminAccessData && tempKey) {
            const transferPayload =
              sessionStorage.getItem(tempKey) ||
              localStorage.getItem(tempKey);
            if (transferPayload) {
              sessionStorage.setItem('adminAccessData', transferPayload);
              adminAccessData = transferPayload;
              try {
                sessionStorage.removeItem(tempKey);
                localStorage.removeItem(tempKey);
                const pointer = localStorage.getItem('latestAdminAccessTempKey');
                if (pointer === tempKey) {
                  localStorage.removeItem('latestAdminAccessTempKey');
                }
              } catch (_) {
                // ignore cleanup issues
              }
            }
          }

          if (!adminAccessData && tempKey) {
            const latestKey = localStorage.getItem('latestAdminAccessTempKey');
            if (latestKey) {
              const latestPayload = localStorage.getItem(latestKey);
              if (latestPayload) {
                sessionStorage.setItem('adminAccessData', latestPayload);
                adminAccessData = latestPayload;
                try {
                  localStorage.removeItem(latestKey);
                  localStorage.removeItem('latestAdminAccessTempKey');
                } catch (_) {
                  // ignore cleanup issues
                }
              }
            }
          }

          // IMPORTANT: Clear cache when viewing different user's profile
          // This prevents showing cached data from previously viewed users
          cacheManager.remove('dashboard_businesses');
          logger.log('🗑️ Cleared dashboard cache for fresh user data');

          if (adminAccessData) {
            const accessInfo = JSON.parse(adminAccessData);
            logger.log('✅ Admin access verified:', accessInfo);

            // Load GMB accounts from admin access data instead of API
            if (accessInfo.gmbAccounts && accessInfo.gmbAccounts.length > 0) {
              logger.log('📊 Loading GMB accounts from admin session:', accessInfo.gmbAccounts.length);

              // Store user context for the session (ONLY in sessionStorage, NOT localStorage)
              try {
                // Create a temporary user session context for admin viewing
                const userContext = {
                  userId: accessInfo.targetUserId,
                  userName: accessInfo.targetUserName,
                  userEmail: accessInfo.targetUserEmail,
                  isAdminView: true,
                  adminId: accessInfo.originalAdminId
                };
                sessionStorage.setItem('currentUserContext', JSON.stringify(userContext));
                logger.log('✅ User context set for admin viewing (sessionStorage only)');

                // Set default profile via context so downstream pages stay in sync
                if (accessInfo.defaultProfile) {
                  setDefaultProfile(accessInfo.defaultProfile);
                  logger.log('Default profile applied via context (admin session only)');
                } else {
                  clearDefaultProfile();
                }

                // Set user's Google access token for API calls (sessionStorage preferred)
                if (accessInfo.googleAccessToken) {
                  logger.log('🔑 Setting user\'s Google access token for admin session');
                  sessionStorage.setItem('googleAccessToken', accessInfo.googleAccessToken);
                  // IMPORTANT: Don't overwrite localStorage - store with unique key
                  sessionStorage.setItem('adminView_googleAccessToken', accessInfo.googleAccessToken);
                  tokenManager.set('google', {
                    access_token: accessInfo.googleAccessToken,
                    token_type: 'Bearer'
                  });
                  logger.log('✅ Google access token configured - admin can now make API calls as user');
                } else {
                  logger.warn('⚠️ No Google access token available for this user');
                }
              } catch (e) {
                logger.warn('Failed to set user context:', e);
              }

              // Transform GMB accounts to business format - these are just initial placeholders
              // We'll fetch fresh data (scores, plans, verification) from API below
              const gmbAccountsFromAdmin = accessInfo.gmbAccounts;
              const accessToken = accessInfo.googleAccessToken;

              // Deduplicate GMB accounts by location_id
              const seenLocationIds = new Set();
              const uniqueGmbAccounts = gmbAccountsFromAdmin.filter(account => {
                const locationId = String(account.location_id).replace('locations/', '');
                if (seenLocationIds.has(locationId)) {
                  return false;
                }
                seenLocationIds.add(locationId);
                return true;
              });

              logger.log('[Admin Access] Deduplicated GMB accounts:', gmbAccountsFromAdmin.length, '->', uniqueGmbAccounts.length);

              // Don't set businesses yet - we need to fetch fresh data from API
              // Instead, we'll use uniqueGmbAccounts to build the location list
              // and then fetch scores, plans, and verification status from API

              setAccountName(accessInfo.targetUserName || 'User Account');

              // Build allLocations array similar to what GMBService returns
              const allLocations = uniqueGmbAccounts.map(account => {
                const locationData = typeof account.location_data === 'string'
                  ? JSON.parse(account.location_data)
                  : account.location_data;

                return {
                  account: {
                    name: `accounts/${account.account_id}`,
                    accountName: accessInfo.targetUserName,
                    type: 'PERSONAL'
                  },
                  loc: {
                    name: `locations/${String(account.location_id).replace('locations/', '')}`,
                    title: locationData?.title || locationData?.name || 'Business',
                    storefrontAddress: locationData?.storefrontAddress,
                    ...locationData
                  }
                };
              });

              logger.log('📊 [Admin Access] Built locations from admin data, now fetching fresh API data...');

              // Fetch saved profile scores from database
              let savedScores = [];
              try {
                const scoresResponse = await api.get('/customer/gmb-profile-socre/get-gmb-profile-score');
                const responseData = scoresResponse?.data;
                if (responseData?.data && Array.isArray(responseData.data)) {
                  savedScores = responseData.data;
                } else if (Array.isArray(responseData)) {
                  savedScores = responseData;
                }
                logger.log('[Admin Access] Fetched saved profile scores:', savedScores.length);
              } catch (scoreErr) {
                logger.warn('[Admin Access] Failed to fetch profile scores:', scoreErr);
              }

              // Fetch competitor scores from database
              let competitorScoresByLocation = {};
              try {
                const locationIds = allLocations.map(({ loc }) => loc.name?.split('/').pop()).filter(Boolean);
                if (locationIds.length > 0) {
                  const compResponse = await api.post('/competitor-analysis/scores-by-locations', { locationIds });
                  if (compResponse?.data?.data) {
                    competitorScoresByLocation = compResponse.data.data;
                  } else if (compResponse?.data && typeof compResponse.data === 'object') {
                    competitorScoresByLocation = compResponse.data;
                  }
                  logger.log('[Admin Access] Fetched competitor scores for', Object.keys(competitorScoresByLocation).length, 'locations');
                }
              } catch (compErr) {
                logger.warn('[Admin Access] Failed to fetch competitor scores:', compErr);
              }

              // Fetch subscription/plan data
              let subscriptionData = {};
              try {
                const locationIds = allLocations.map(({ loc }) => loc.name?.split('/').pop()).filter(Boolean);
                if (locationIds.length > 0) {
                  const resp = await api.post('/payments/gmb/bulk', { locationIds });
                  if (resp && (resp.data || resp.success)) {
                    subscriptionData = resp.data?.data || resp.data || resp.dataMap || resp.data?.subscriptions || {};
                  }
                }
                logger.log('[Admin Access] Fetched subscription data');
              } catch (subErr) {
                logger.warn('[Admin Access] Failed to fetch subscriptions:', subErr);
              }

              setSubscriptionsByGmb(subscriptionData);

              // Normalize locations with fresh data from API
              const normalized = await Promise.all(allLocations.map(async ({ account, loc }) => {
                const id = loc.name?.split('/').pop();
                const address = loc.storefrontAddress?.addressLines?.join(', ') ||
                  loc.storefrontAddress?.formattedAddress ||
                  loc.storefrontAddress?.locality ||
                  'Address not available';

                // Fetch verification status
                let simplifiedStatus = 'unknown';
                let hasVOM = false;
                try {
                  if (accessToken) {
                    const vom = await GMBService.getVoiceOfMerchantState(id, accessToken);
                    simplifiedStatus = vom?.simplifiedStatus || 'unknown';
                    hasVOM = vom?.raw?.hasVoiceOfMerchant === true;
                  }
                } catch (e) {
                  logger.warn('[Admin Access] VOM fetch failed for', id, e?.message);
                }

                const status = (
                  (hasVOM || simplifiedStatus === 'verified') ? 'verified' :
                    simplifiedStatus === 'suspended' ? 'suspended' :
                      simplifiedStatus === 'pending_verification' ? 'pending_verification' :
                        'unverified'
                );

                // Find saved score
                const savedScore = savedScores.find(s => {
                  const locationMatch = s.location && (s.location.includes(id) || id.includes(s.location?.split('/').pop()));
                  return locationMatch;
                });

                // Get competitor score from database
                const normalizedId = String(id).replace('locations/', '');
                const dbCompetitorScore = competitorScoresByLocation[normalizedId];
                const gmbScore = dbCompetitorScore?.score ? Math.round(dbCompetitorScore.score) : null;

                const scoreValue = savedScore?.total_score;
                const hasOldScore = scoreValue !== null && scoreValue !== undefined && scoreValue !== '' && scoreValue !== '0';

                const optimizationScore = gmbScore
                  ? `${gmbScore}/1000`
                  : (hasOldScore ? `${scoreValue}/300` : 'Check now');

                logger.log('[Admin Access] Location:', loc.title, 'ID:', id, 'Score:', optimizationScore, 'Status:', status);

                return {
                  id,
                  name: loc.title || account?.accountName || 'Business',
                  address,
                  status,
                  optimizationScore,
                  locationId: id,
                  accountType: account?.type || 'UNKNOWN',
                  verificationState: simplifiedStatus,
                  verified: status === 'verified',
                  locationData: loc,
                  account: account
                };
              }));

              setBusinesses(normalized);
              setLoading(false);
              logger.log('✅ [Admin Access] Loaded', normalized.length, 'businesses with fresh API data');
              return; // Skip normal API fetch
            }
          }
        }

        if (tokenFromUrl) {
          try {
            sessionStorage.setItem('googleAccessToken', tokenFromUrl);
            localStorage.setItem('googleAccessToken', tokenFromUrl);
          } catch (_) { }
          tokenManager.set('google', { access_token: tokenFromUrl, token_type: 'Bearer' });
        }
        // If an env token is provided, seed TokenManager for convenience
        if (envGoogleAccessToken) {
          tokenManager.set('google', { access_token: envGoogleAccessToken, token_type: 'Bearer' });
        }

        // Check if we should use live API or fallback to mock
        if (!envUseLiveApi) {
          if (envDebugMode) {
            logger.log('Using mock data - Live API disabled');
            logger.log('Environment config:', {
              useLiveApi: envUseLiveApi,
              useMockGmb: envUseMockGmb,
              businessName: envBusinessName
            });
          }

          // Set mock data for demo purposes - using realistic verification states
          const mockBusinesses = [
            {
              id: '1',
              name: envBusinessName,
              address: '23 Maplewood Lane,IL 62704,USA',
              status: 'unverified', // More realistic default
              optimizationScore: '300/300',
              locationId: '1',
              verificationState: 'UNVERIFIED'
            },
            {
              id: '2',
              name: envBusinessName,
              address: '23 Maplewood Lane,IL 62704,USA',
              status: 'pending_verification',
              optimizationScore: 'Pending',
              locationId: '2',
              verificationState: 'PENDING_VERIFICATION'
            },
            {
              id: '3',
              name: envBusinessName,
              address: '23 Maplewood Lane,IL 62704,USA',
              status: 'verified',
              optimizationScore: 'Check now',
              locationId: '3',
              verificationState: 'VERIFIED'
            }
          ];

          if (envUseMockGmb) {
            setBusinesses(mockBusinesses);
          } else {
            setBusinesses([]);
          }
          setLoading(false);
          return;
        }

        if (envDebugMode) {
          logger.log('Using live GMB API with configuration:', {
            hasClientId: !!envGoogleClientId,
            hasProjectId: !!envGmbProjectId,
            useLiveApi: envUseLiveApi
          });
        }

        // Validate required configuration
        if (!envGmbProjectId) {
          throw new Error('GMB Project ID not configured. Please set REACT_APP_GMB_PROJECT_ID in your .env file.');
        }

        // Get access token (needed for both cache and later operations)
        const accessToken = tokenManager.get('google')?.access_token;

        // cacheManager scopes entries per user/impersonation context automatically
        const cacheKey = 'dashboard_businesses';

        // Try to get from cache first
        const { data: cachedData, fromCache } = await cacheManager.getOrFetch(
          cacheKey,
          async () => {
            logger.log('🔄 Fetching businesses from API...');
            const accounts = await GMBService.getAccounts();

            if (accounts.length === 0) {
              return { accounts: [], allLocations: [], accountName: envBusinessName };
            }

            const allLocations = [];

            for (const acct of accounts) {
              try {
                if (!acct?.name) continue;
                const locs = await GMBService.getLocations(accessToken, acct.name);
                if (Array.isArray(locs) && locs.length) {
                  locs.forEach(l => allLocations.push({ account: acct, loc: l }));
                }
              } catch (e) {
                logger.warn('Failed to fetch locations for account', acct?.name, e?.message || e);
              }
            }

            return {
              accounts,
              allLocations,
              accountName: accounts[0]?.accountName || envBusinessName
            };
          },
          30 * 60 * 1000 // 30 minutes cache
        );

        const { accounts, allLocations, accountName: fetchedAccountName } = cachedData;

        if (fromCache) {
          logger.log('✅ Loaded businesses from cache - instant!');

          // When loading from cache, process and display businesses immediately
          if (accounts.length > 0) {
            setAccountName(fetchedAccountName);
          }

          if (accounts.length === 0) {
            setBusinesses([]);
            setLoading(false);
            return;
          }

          // Process cached locations immediately
          if (allLocations.length === 0) {
            const fallback = accounts.map((account) => {
              let mappedStatus = 'unverified';
              const verificationState = account.verificationState;
              if (verificationState === 'VERIFIED') mappedStatus = 'verified';
              else if (verificationState === 'PENDING_VERIFICATION' || verificationState === 'PENDING') mappedStatus = 'pending_verification';
              else if (verificationState === 'SUSPENDED') mappedStatus = 'suspended';
              return {
                id: account.name?.split('/').pop(),
                name: account.accountName || envBusinessName,
                address: 'Account level - no location data',
                status: mappedStatus,
                optimizationScore: account.vettedState || 'N/A',
                locationId: account.name?.split('/').pop(),
                accountType: account.type || 'UNKNOWN',
                verificationState: account.verificationState || 'UNKNOWN',
                vettedState: account.vettedState || 'UNKNOWN',
                account: account  // ← Added: Account object
              };
            });
            setBusinesses(fallback);
          } else {
            // Fetch saved profile scores from database for cached data
            let savedScores = [];
            try {
              const scoresResponse = await api.get('/customer/gmb-profile-socre/get-gmb-profile-score');
              logger.log('Profile scores API response:', scoresResponse);
              // API returns: { data: { data: rows, pagination: {...} }, message: '...' }
              // After api service processing it becomes: { data: { data: rows, pagination }, ... }
              const responseData = scoresResponse?.data;
              if (responseData?.data && Array.isArray(responseData.data)) {
                savedScores = responseData.data;
              } else if (Array.isArray(responseData)) {
                savedScores = responseData;
              }
              logger.log('Saved scores found:', savedScores.length, savedScores);
            } catch (scoreErr) {
              logger.warn('Failed to fetch profile scores:', scoreErr);
            }

            // Fetch competitor scores from database (not localStorage cache)
            let competitorScoresByLocation = {};
            try {
              const locationIds = allLocations.map(({ loc }) => loc.name?.split('/').pop()).filter(Boolean);
              if (locationIds.length > 0) {
                const compResponse = await api.post('/competitor-analysis/scores-by-locations', { locationIds });
                logger.log('Competitor scores API response (cached mode):', compResponse);
                if (compResponse?.data?.data) {
                  competitorScoresByLocation = compResponse.data.data;
                } else if (compResponse?.data && typeof compResponse.data === 'object') {
                  competitorScoresByLocation = compResponse.data;
                }
                logger.log('Fetched competitor scores from DB for', Object.keys(competitorScoresByLocation).length, 'locations');
              }
            } catch (compErr) {
              logger.warn('Failed to fetch competitor scores from DB:', compErr);
            }

            // ✅ FIX: Fetch FRESH verification status from API even when using cached location data
            // This ensures verification status is always accurate and not stale from cache
            logger.log('🔄 [Cache Mode] Fetching fresh verification status for cached locations...');
            const normalized = await Promise.all(allLocations.map(async ({ account, loc }) => {
              const id = loc.name?.split('/').pop();
              const address = loc.storefrontAddress?.addressLines?.join(', ') ||
                loc.storefrontAddress?.locality ||
                loc.storefrontAddress?.administrativeArea ||
                'Address not available';

              // Find saved score for this location - try multiple matching strategies
              const savedScore = savedScores.find(s => {
                const locationMatch = s.location && (s.location.includes(id) || id.includes(s.location?.split('/').pop()));
                const nameMatch = s.business_name_contains_city && loc.title && s.business_name_contains_city.toLowerCase() === loc.title.toLowerCase();
                return locationMatch || nameMatch;
              });

              // Get competitor analysis GMB score from database (out of 1000)
              const normalizedId = String(id).replace('locations/', '');
              const dbCompetitorScore = competitorScoresByLocation[normalizedId];
              const gmbScore = dbCompetitorScore?.score ? Math.round(dbCompetitorScore.score) : null;

              // Fall back to profile strength score (out of 300) if no GMB score
              const scoreValue = savedScore?.total_score;
              const hasOldScore = scoreValue !== null && scoreValue !== undefined && scoreValue !== '' && scoreValue !== '0';

              // Use GMB score if available, otherwise use old score
              const optimizationScore = gmbScore
                ? `${gmbScore}/1000`
                : (hasOldScore ? `${scoreValue}/300` : 'Check now');
              logger.log('Location:', loc.title, 'ID:', id, 'GMB Score:', gmbScore, 'DB Competitor:', dbCompetitorScore, 'Old Score:', scoreValue, 'Final:', optimizationScore);

              // ✅ ALWAYS fetch fresh verification status from VoiceOfMerchant API
              let simplifiedStatus = 'unknown';
              let hasVOM = false;
              let hasBA = false;
              try {
                const vom = await GMBService.getVoiceOfMerchantState(id, accessToken);
                simplifiedStatus = vom?.simplifiedStatus || 'unknown';
                hasVOM = vom?.raw?.hasVoiceOfMerchant === true;
                hasBA = vom?.raw?.hasBusinessAuthority === true;
                if (envDebugMode) logger.log('VOM state for (cached)', id, { simplifiedStatus, hasVOM, hasBA, raw: vom?.raw });
              } catch (e) {
                logger.warn('VOM fetch failed for (cached)', id, e?.message || e);
              }

              const status = (
                (hasVOM || simplifiedStatus === 'verified') ? 'verified' :
                  simplifiedStatus === 'suspended' ? 'suspended' :
                    simplifiedStatus === 'pending_verification' ? 'pending_verification' :
                      'unverified'
              );

              return {
                id,
                name: loc.title || account?.accountName || envBusinessName,
                address,
                status,  // ✅ Now uses FRESH verification status from API
                optimizationScore,
                locationId: id,
                accountType: account?.type || 'LOCATION',
                verificationState: simplifiedStatus,
                vettedState: account?.vettedState || 'UNKNOWN',
                verified: status === 'verified',
                locationState: loc?.locationState || null,
                metadata: loc?.metadata || null,
                locationData: loc,
                account: account
              };
            }));
            setBusinesses(normalized);
          }

          // Set loading to false immediately when using cache
          setLoading(false);
          return; // Exit early - no need to process further
        } else {
          logger.log('🔄 Fetched businesses from API - cached for next time');
        }

        if (accounts.length > 0) {
          setAccountName(fetchedAccountName);
        }

        if (accounts.length === 0) {
          if (envDebugMode) {
            logger.log('No GMB accounts found');
          }
          setBusinesses([]);
          setLoading(false);
          return;
        }

        if (envDebugMode) {
          logger.log('Found GMB accounts:', accounts.length);
          logger.log('First account details:', accounts[0]);
        }

        if (envDebugMode) {
          logger.log('Total locations found across accounts:', allLocations.length);
        }

        // Sync GMB locations to database for task assignment
        if (allLocations.length > 0) {
          try {
            const session = getSession();
            const userId = session?.id;

            logger.log('📊 Sync Check:', {
              hasLocations: allLocations.length,
              hasSession: !!session,
              userId: userId,
              firstLocation: allLocations[0]?.loc?.title
            });

            if (userId) {
              logger.log(`🔄 Syncing ${allLocations.length} GMB profiles to database for user ${userId}...`);

              // Extract just the location data for syncing
              const locationsToSync = allLocations.map(({ account, loc }) => ({
                ...loc,
                accountId: account?.name?.split('/').pop(),
                account_id: account?.name?.split('/').pop()
              }));

              logger.log('📦 Locations to sync:', locationsToSync.map(l => l.title || l.name));

              const result = await GMBSyncService.syncMultipleLocations(userId, locationsToSync);
              logger.log('✅ GMB profiles synced successfully:', result);

              // CRITICAL FIX: Update businesses with plan data from sync result
              // The sync result contains the plan assigned by admin, which is the source of truth
              if (result && Array.isArray(result)) {
                const planMap = {};
                result.forEach(syncedProfile => {
                  if (syncedProfile.location_id && syncedProfile.plan) {
                    // Store plan by location_id (handle both with and without prefix)
                    const locId = String(syncedProfile.location_id).replace('locations/', '');
                    planMap[locId] = syncedProfile.plan;
                    planMap[`locations/${locId}`] = syncedProfile.plan;
                  }
                });

                // Update businesses state with plan data
                setBusinesses(prev => prev.map(business => {
                  const locId = String(business.locationId || business.id).replace('locations/', '');
                  const plan = planMap[locId] || planMap[`locations/${locId}`] || business.plan;
                  if (plan && plan !== business.plan) {
                    logger.log(`📋 Updating business ${business.name} with plan: ${plan}`);
                    return { ...business, plan };
                  }
                  return business;
                }));
              }
            } else {
              logger.warn('⚠️ No user ID found in session, skipping GMB sync');
              logger.warn('Session data:', session);
            }
          } catch (syncError) {
            logger.error('❌ Failed to sync GMB profiles:', syncError);
            logger.error('Error details:', syncError.message, syncError.stack);
            // Don't block the UI if sync fails
          }
        } else {
          logger.warn('⚠️ No GMB locations found to sync');
        }

        if (allLocations.length === 0) {
          // Fallback: present accounts as rows if no locations are returned
          const fallback = accounts.map((account) => {
            let mappedStatus = 'unverified';
            const verificationState = account.verificationState;
            if (verificationState === 'VERIFIED') mappedStatus = 'verified';
            else if (verificationState === 'PENDING_VERIFICATION' || verificationState === 'PENDING') mappedStatus = 'pending_verification';
            else if (verificationState === 'SUSPENDED') mappedStatus = 'suspended';
            return {
              id: account.name?.split('/').pop(),
              name: account.accountName || envBusinessName,
              address: 'Account level - no location data',
              status: mappedStatus,
              optimizationScore: account.vettedState || 'N/A',
              locationId: account.name?.split('/').pop(),
              accountType: account.type || 'UNKNOWN',
              verificationState: account.verificationState || 'UNKNOWN',
              vettedState: account.vettedState || 'UNKNOWN',
              account: account  // ← Added: Account object
            };
          });
          setBusinesses(fallback);
          setLoading(false);
          return;
        }

        // Fetch saved profile scores from database
        let savedScores = [];
        try {
          const scoresResponse = await api.get('/customer/gmb-profile-socre/get-gmb-profile-score');
          logger.log('Profile scores API response (live):', scoresResponse);
          const responseData = scoresResponse?.data;
          if (responseData?.data && Array.isArray(responseData.data)) {
            savedScores = responseData.data;
          } else if (Array.isArray(responseData)) {
            savedScores = responseData;
          }
          logger.log('Fetched saved profile scores:', savedScores.length, savedScores);
        } catch (scoreErr) {
          logger.warn('Failed to fetch profile scores:', scoreErr);
        }

        // Fetch competitor scores from database (not localStorage cache)
        let competitorScoresByLocation = {};
        try {
          const locationIds = allLocations.map(({ loc }) => loc.name?.split('/').pop()).filter(Boolean);
          if (locationIds.length > 0) {
            const compResponse = await api.post('/competitor-analysis/scores-by-locations', { locationIds });
            logger.log('Competitor scores API response:', compResponse);
            if (compResponse?.data?.data) {
              competitorScoresByLocation = compResponse.data.data;
            } else if (compResponse?.data && typeof compResponse.data === 'object') {
              competitorScoresByLocation = compResponse.data;
            }
            logger.log('Fetched competitor scores from DB for', Object.keys(competitorScoresByLocation).length, 'locations');
          }
        } catch (compErr) {
          logger.warn('Failed to fetch competitor scores from DB:', compErr);
        }

        // Normalize locations to BusinessTable shape using VoiceOfMerchantState for status
        const normalized = await Promise.all(allLocations.map(async ({ account, loc }) => {
          const id = loc.name?.split('/').pop();
          const address = loc.storefrontAddress?.addressLines?.join(', ') ||
            loc.storefrontAddress?.locality ||
            loc.storefrontAddress?.administrativeArea ||
            'Address not available';
          let simplifiedStatus = 'unknown';
          let hasVOM = false;
          let hasBA = false;
          try {
            const vom = await GMBService.getVoiceOfMerchantState(id, accessToken);
            simplifiedStatus = vom?.simplifiedStatus || 'unknown';
            hasVOM = vom?.raw?.hasVoiceOfMerchant === true;
            hasBA = vom?.raw?.hasBusinessAuthority === true;
            if (envDebugMode) logger.log('VOM state for', id, { simplifiedStatus, hasVOM, hasBA, raw: vom?.raw });
          } catch (e) {
            logger.warn('VOM fetch failed for', id, e?.message || e);
          }

          const status = (
            (hasVOM || simplifiedStatus === 'verified') ? 'verified' :
              simplifiedStatus === 'suspended' ? 'suspended' :
                simplifiedStatus === 'pending_verification' ? 'pending_verification' :
                  'unverified'
          );

          // Find saved score for this location - try multiple matching strategies
          const savedScore = savedScores.find(s => {
            const locationMatch = s.location && (s.location.includes(id) || id.includes(s.location?.split('/').pop()));
            const nameMatch = s.business_name_contains_city && loc.title && s.business_name_contains_city.toLowerCase() === loc.title.toLowerCase();
            return locationMatch || nameMatch;
          });

          // Get competitor analysis GMB score from database (out of 1000)
          // Try to match by location ID (normalized without 'locations/' prefix)
          const normalizedId = String(id).replace('locations/', '');
          const dbCompetitorScore = competitorScoresByLocation[normalizedId];
          const gmbScore = dbCompetitorScore?.score ? Math.round(dbCompetitorScore.score) : null;

          // Fall back to profile strength score (out of 300) if no GMB score
          const scoreValue = savedScore?.total_score;
          const hasOldScore = scoreValue !== null && scoreValue !== undefined && scoreValue !== '' && scoreValue !== '0';

          // Use GMB score if available, otherwise use old score
          const optimizationScore = gmbScore
            ? `${gmbScore}/1000`
            : (hasOldScore ? `${scoreValue}/300` : 'Check now');
          logger.log('Location (live):', loc.title, 'ID:', id, 'GMB Score:', gmbScore, 'DB Competitor:', dbCompetitorScore, 'Old Score:', scoreValue, 'Final:', optimizationScore);

          return {
            id,
            name: loc.title || account?.accountName || envBusinessName,
            address,
            status,
            optimizationScore,
            locationId: id,
            accountType: account?.type || 'UNKNOWN',
            verificationState: simplifiedStatus,
            vettedState: account?.vettedState || 'UNKNOWN',
            verified: status === 'verified',
            locationState: loc?.locationState || null,
            metadata: loc?.metadata || null,
            locationData: loc,  // ← Added: Full location data
            account: account    // ← Added: Account object with ID
          };
        }));

        // Fetch subscription/payment status for displayed locations
        try {
          const locationIds = normalized.map(b => b.locationId).filter(Boolean);
          if (locationIds.length > 0) {
            const resp = await api.post('/payments/gmb/bulk', { locationIds });
            if (resp && (resp.data || resp.success)) {
              // API may return mapping in resp.data or resp.data.data depending on ApiService
              const dataMap = resp.data?.data || resp.data || resp.dataMap || resp.data?.subscriptions || resp;
              setSubscriptionsByGmb(dataMap || {});
            } else {
              setSubscriptionsByGmb({});
            }
          }
        } catch (err) {
          console.warn('Failed to fetch subscriptions for dashboard businesses', err);
          setSubscriptionsByGmb({});
        }

        setBusinesses(normalized);
        setLoading(false);
        return;

      } catch (err) {
        logger.error('Error fetching businesses:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinesses();
    // we want to re-run if query string changes (token passed)
  }, [location.search, setDefaultProfile, clearDefaultProfile]);

  // ============================================
  // AUTO-SELECT DEFAULT PROFILE LOGIC
  // 1. Single profile: auto-select it as default
  // 2. Multiple profiles: auto-select the one with active subscription/trial
  // ============================================
  useEffect(() => {
    const autoSelectDefaultProfile = async () => {
      // Skip if still loading or no businesses
      if (loading || businesses.length === 0) return;

      // Check if default profile is already set in localStorage
      const existingDefault = localStorage.getItem(`visibeen_default_profile_${getSession()?.id || 'anonymous'}`);
      if (existingDefault) {
        try {
          const parsed = JSON.parse(existingDefault);
          // Verify the existing default is still in the businesses list
          const isValid = businesses.some(b => {
            const bLocId = String(b.locationId || b.id);
            const pLocId = String(parsed.locationId || parsed.id);
            return bLocId === pLocId;
          });
          if (isValid) {
            logger.log('[Dashboard] Default profile already set and valid, skipping auto-select');
            return;
          }
        } catch (e) {
          // Invalid stored profile, will auto-select
        }
      }

      logger.log(`[Dashboard] Auto-select: ${businesses.length} business(es) found`);

      // Case 1: Single profile - auto-select it
      if (businesses.length === 1) {
        const business = businesses[0];
        const profileData = {
          id: business.id,
          locationId: business.locationId || business.id,
          name: business.name,
          address: business.address,
          status: business.status,
          verified: business.verified,
          accountId: business.account?.name?.split('/').pop() || null,
          locationData: business.locationData,
          account: business.account
        };
        logger.log('[Dashboard] Auto-selecting single profile as default:', profileData.name);
        setDefaultProfile(profileData);
        return;
      }

      // Case 2: Multiple profiles - find one with active subscription/trial
      try {
        const locationIds = businesses.map(b => b.locationId || b.id).filter(Boolean);

        // Fetch subscription status for all profiles
        const resp = await api.post('/customer/subscription/profiles/bulk', { locationIds });
        const subsData = resp?.data?.data || resp?.data || {};

        logger.log('[Dashboard] Bulk subscription data:', subsData);

        // Find profile with active trial (priority) or active subscription
        let selectedProfile = null;
        let selectedIsTrial = false;

        for (const business of businesses) {
          const locId = String(business.locationId || business.id);
          const subInfo = subsData[locId] || subsData[`locations/${locId}`];

          if (subInfo && subInfo.hasSubscription) {
            if (subInfo.isTrial) {
              // Trial has highest priority - select immediately
              selectedProfile = business;
              selectedIsTrial = true;
              logger.log(`[Dashboard] Found trial profile: ${business.name}`);
              break;
            } else if (!selectedProfile) {
              // Active subscription - select if no trial found yet
              selectedProfile = business;
              logger.log(`[Dashboard] Found subscribed profile: ${business.name}`);
            }
          }
        }

        // If found a profile with subscription/trial, set it as default
        if (selectedProfile) {
          const profileData = {
            id: selectedProfile.id,
            locationId: selectedProfile.locationId || selectedProfile.id,
            name: selectedProfile.name,
            address: selectedProfile.address,
            status: selectedProfile.status,
            verified: selectedProfile.verified,
            accountId: selectedProfile.account?.name?.split('/').pop() || null,
            locationData: selectedProfile.locationData,
            account: selectedProfile.account
          };
          logger.log(`[Dashboard] Auto-selecting ${selectedIsTrial ? 'trial' : 'subscribed'} profile as default:`, profileData.name);
          setDefaultProfile(profileData);
        } else {
          logger.log('[Dashboard] No profile with active subscription found, not auto-selecting');
        }
      } catch (err) {
        logger.warn('[Dashboard] Failed to fetch subscription data for auto-select:', err?.message);
      }
    };

    autoSelectDefaultProfile();
  }, [loading, businesses, setDefaultProfile]);

  if (loading) {
    return (
      <DashboardLayout>
        <PageHeader
          title="Businesses"
          subtitle="All Google Business Profile locations across your connected accounts."
        />
        <LoadingContainer>
          <CircularProgress size={40} sx={{ color: '#0B91D6' }} />
          <Typography variant="body1" color="textSecondary">
            Loading GMB accounts...
          </Typography>
        </LoadingContainer>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <PageHeader
          title="Businesses"
          subtitle="All Google Business Profile locations across your connected accounts."
        />
        <Alert severity="error" sx={{ marginBottom: '24px' }}>
          Error loading GMB accounts: {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ backgroundColor: '#0B91D6' }}
        >
          Retry
        </Button>
      </DashboardLayout>
    );
  }
  // Check if admin is viewing user's profile
  const adminAccessData = sessionStorage.getItem('adminAccessData');


  return (
    <DashboardLayout>
      <Box sx={{
        p: 4,
        background: '#fff',
        minHeight: '100vh'
      }}>
        <PageHeader
          title={`Welcome, ${accountName}`}
          subtitle="All Google Business Profile locations across your connected accounts."
        />

        {businesses.length === 0 ? (
          <Box sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            padding: { xs: '32px 24px', md: '48px 56px' },
            textAlign: 'center',
            color: '#fff',
            boxShadow: '0 20px 60px rgba(102, 126, 234, 0.4)',
            position: 'relative',
            overflow: 'hidden',
            maxWidth: '700px',
            margin: '40px auto'
          }}>
            {/* Decorative circles */}
            <Box sx={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              zIndex: 0
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              zIndex: 0
            }} />

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* Icon */}
              <Box sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '40px',
                backdropFilter: 'blur(10px)'
              }}>
                🏪
              </Box>

              <Typography variant="h4" sx={{
                fontWeight: 800,
                mb: 2,
                textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                fontSize: { xs: '1.75rem', md: '2.125rem' }
              }}>
                No GMB Profile Yet?
              </Typography>

              <Typography variant="h6" sx={{
                fontWeight: 400,
                mb: 3,
                opacity: 0.95,
                lineHeight: 1.6,
                maxWidth: '500px',
                margin: '0 auto 24px'
              }}>
                Let <strong>Visibeen</strong> create a brand new Google Business Profile for your business — fully optimized and ready to attract customers!
              </Typography>

              {/* Features */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 2, md: 4 },
                mb: 4,
                flexWrap: 'wrap'
              }}>
                {['✅ Complete Setup', '✅ SEO Optimized', '✅ Expert Support'].map((feature, i) => (
                  <Typography key={i} variant="body2" sx={{
                    background: 'rgba(255,255,255,0.15)',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontWeight: 500,
                    backdropFilter: 'blur(5px)'
                  }}>
                    {feature}
                  </Typography>
                ))}
              </Box>

              {/* CTA Button */}
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/requests')}
                sx={{
                  background: 'linear-gradient(135deg, #fff 0%, #f0f0f0 100%)',
                  color: '#667eea',
                  fontWeight: 700,
                  fontSize: '16px',
                  padding: '14px 40px',
                  borderRadius: '50px',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 35px rgba(0,0,0,0.3)',
                    background: '#fff'
                  }
                }}
              >
                <StorefrontIcon sx={{ fontSize: 28, color: '#fff' }} /> Request Your GMB Profile
              </Button>

              <Typography variant="caption" sx={{
                display: 'block',
                mt: 2,
                opacity: 0.8
              }}>
                Starting at ₹10,000 • One-time setup fee
              </Typography>
            </Box>
          </Box>
        ) : (
          <BusinessTable businesses={businesses} subscriptions={subscriptionsByGmb} />
        )}
      </Box>

    </DashboardLayout>
  );
};

export default Dashboard;
